import { Check } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Fragment, type ReactNode, useState } from "react"
import { twMerge } from "tailwind-merge"
import { BASE_PATH } from "@/constants/app.mjs"

const pricingTableThCss =
  "p-[12px] font-medium text-[20px] text-center first:text-left [&[data-is-selected='true']]:bg-[rgba(255,255,255,0.05)] [&[data-is-selected='true']]:border-t [&[data-is-selected='true']]:border-solid [&[data-is-selected='true']]:border-l-[1px] [&[data-is-selected='true']]:border-r-[1px]"

const pricingTableTdCss =
  "p-[12px] text-center border-t border-solid border-border-default text-sm first:text-left first:w-[400px] [&[data-is-selected='true']]:bg-[rgba(255,255,255,0.05)] [&[data-is-selected='true']]:border-l [&[data-is-selected='true']]:border-r [&[data-is-selected='true']]:border-solid [&[data-is-selected='true']]:border-border-default [tr:last-child&[data-is-selected='true']]:border-b"

const tableCss = "w-full border-collapse text-left text-fg-default"

type PricingTableProps = {
  selectedProduct?: "pro" | "api" | "datashare"
}

const CheckIcon = ({ highlighted = false }: { highlighted?: boolean }) => (
  <Check
    size={highlighted ? 18 : 16}
    weight="bold"
    className="inline-block transition-all text-fg-emerald"
  />
)

const pricingTiers = [
  {
    label: "Free",
    id: "free",
  },
  {
    label: "Pro",
    id: "pro",
  },
  {
    label: "API",
    id: "api",
  },
  {
    label: "Datashare",
    id: "datashare",
  },
] as const

const featureGroups: Array<{
  headingText: string
  features: Array<{
    feature: ReactNode
    free: boolean | string
    pro: boolean | string
    api: boolean | string
    datashare: boolean | string
  }>
}> = [
  {
    headingText: "Data access",
    features: [
      {
        feature: "All historical data",
        free: true,
        pro: true,
        api: true,
        datashare: true,
      },
      {
        feature: "CSV data exports",
        free: false,
        pro: true,
        api: true,
        datashare: true,
      },
      {
        feature: (
          <span className="flex items-center gap-2">
            Access to MCP
            <span className="text-xs font-medium text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">
              New
            </span>
          </span>
        ),
        free: false,
        pro: true,
        api: true,
        datashare: true,
      },
      {
        feature: "Team access",
        free: false,
        pro: true,
        api: true,
        datashare: true,
      },
    ],
  },
  {
    headingText: "Studio",
    features: [
      {
        feature: "Custom dashboards",
        free: "3",
        pro: "Unlimited",
        api: "Unlimited",
        datashare: "Unlimited",
      },
      {
        feature: "Custom charts",
        free: "3",
        pro: "Unlimited",
        api: "Unlimited",
        datashare: "Unlimited",
      },
    ],
  },
  {
    headingText: "Sheets",
    features: [
      {
        feature: "Google Sheets plugin",
        free: false,
        pro: true,
        api: true,
        datashare: true,
      },
      {
        feature: "Excel plugin",
        free: false,
        pro: true,
        api: true,
        datashare: true,
      },
      {
        feature: "Rate limits",
        free: false,
        pro: "500k / month",
        api: "1M / month",
        datashare: "Custom",
      },
    ],
  },
  {
    headingText: "API",
    features: [
      {
        feature: "Access to REST API",
        free: false,
        pro: false,
        api: true,
        datashare: true,
      },
      {
        feature: "Python & TypeScript SDKs",
        free: false,
        pro: false,
        api: true,
        datashare: true,
      },
      {
        feature: "Rate limits",
        free: false,
        pro: false,
        api: "250k / day",
        datashare: "Custom",
      },
    ],
  },
  {
    headingText: "Data Warehouse",
    features: [
      {
        feature: "Access to raw blockchain data",
        free: false,
        pro: false,
        api: false,
        datashare: true,
      },
      {
        feature: "BigQuery data sharing",
        free: false,
        pro: false,
        api: false,
        datashare: true,
      },
      {
        feature: "Snowflake data sharing",
        free: false,
        pro: false,
        api: false,
        datashare: true,
      },
    ],
  },
  {
    headingText: "Support",
    features: [
      {
        feature: "Chat support",
        free: true,
        pro: true,
        api: true,
        datashare: true,
      },
      {
        feature: "Slack / Telegram support",
        free: false,
        pro: false,
        api: true,
        datashare: true,
      },
      {
        feature: "SLA",
        free: false,
        pro: false,
        api: false,
        datashare: true,
      },
    ],
  },
]

const productColumnWidth = "220px"

const CellContents = ({
  value,
  highlighted = false,
}: {
  value: boolean | ReactNode
  highlighted?: boolean
}) => {
  if (value === true) {
    return <CheckIcon highlighted={highlighted} />
  } else if (value === false) {
    return null
  } else if (typeof value === "string") {
    return <div className="text-sm text-fg-secondary">{value}</div>
  }

  return value
}

type Feature = (typeof featureGroups)[number]["features"][number]

const PricingTable = ({ selectedProduct }: PricingTableProps) => {
  const [hoveredFeature, setHoveredFeature] = useState<Feature | null>(null)

  const isTierActive = (tierId: (typeof pricingTiers)[number]["id"]) => {
    if (!hoveredFeature) return false
    const value = hoveredFeature[tierId]
    return value === true || typeof value === "string"
  }

  return (
    <div className="-mx-5 px-3 lg:px-0 [@media(max-width:1024px)]:overflow-x-auto">
      <table className={tableCss} style={{}}>
        <thead>
          <tr>
            <th
              className={twMerge(pricingTableThCss, "bg-bg-default")}
              style={{
                position: "sticky",
                top: 70,
                zIndex: 2,
              }}
            />
            {pricingTiers.map((tier) => (
              <th
                key={tier.id}
                className={twMerge(
                  pricingTableThCss,
                  "bg-bg-default",
                  "transition-all",
                  hoveredFeature && !isTierActive(tier.id) ? "text-white/40" : "",
                )}
                data-is-selected={selectedProduct === tier.id}
                style={{
                  width: productColumnWidth,
                  position: "sticky",
                  top: 70,
                  zIndex: 2,
                  fontWeight: isTierActive(tier.id) ? "600" : "500",
                  fontSize: "16px",
                }}
              >
                {tier.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {featureGroups.map((featureGroup, index) => (
            <Fragment key={featureGroup.headingText}>
              <tr key={index} style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                <td className={pricingTableTdCss}>
                  <div className="text-base font-medium text-fg-default">
                    {featureGroup.headingText}
                  </div>
                </td>
                {pricingTiers.map((tier) => (
                  <td key={tier.id} className={pricingTableTdCss} />
                ))}
              </tr>
              {featureGroup.features.map((feature, index) => (
                <tr
                  key={index}
                  className="hover:bg-[rgba(255,255,255,0.03)] transition-colors"
                  onMouseEnter={() => setHoveredFeature(feature)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <td className={twMerge(pricingTableTdCss, "border-border-default")}>
                    {feature.feature}
                  </td>
                  {pricingTiers.map((tier) => (
                    <td
                      key={tier.id}
                      data-is-selected={selectedProduct === tier.id}
                      className={pricingTableTdCss}
                      style={{}}
                    >
                      <CellContents
                        value={feature[tier.id]}
                        highlighted={hoveredFeature === feature}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
          <tr>
            <td
              className={twMerge(pricingTableTdCss, "border-border-default")}
              style={{ paddingTop: "40px" }}
            ></td>
            <td className={pricingTableTdCss} style={{ paddingTop: "60px" }}>
              <LinkButton className="w-full" href={`${BASE_PATH}/explorer/auth/signup`}>
                Sign up for Free
              </LinkButton>
            </td>
            <td
              className={pricingTableTdCss}
              data-is-selected={selectedProduct === "pro"}
              style={{ paddingTop: "60px" }}
            >
              <LinkButton
                color="success"
                className="w-full"
                href={`${BASE_PATH}/explorer/auth/signup`}
              >
                Start 7 day trial
              </LinkButton>
            </td>
            <td
              className={pricingTableTdCss}
              data-is-selected={selectedProduct === "api"}
              style={{ paddingTop: "60px" }}
            >
              <LinkButton
                className="w-full"
                href={`https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo`}
              >
                Book a demo
              </LinkButton>
            </td>
            <td
              className={pricingTableTdCss}
              data-is-selected={selectedProduct === "datashare"}
              style={{ paddingTop: "60px" }}
            >
              <LinkButton
                className="w-full"
                href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
              >
                Book a demo
              </LinkButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default PricingTable
